import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

const LAYERS = ['ALL', 'COMPUTE', 'IDENTITY', 'NETWORK', 'DATABASE', 'CODE'];

const LAYER_BADGE = {
  COMPUTE:  { bg: '#dbeafe', color: '#1d4ed8' },
  IDENTITY: { bg: '#ede9fe', color: '#6d28d9' },
  NETWORK:  { bg: '#d1fae5', color: '#065f46' },
  DATABASE: { bg: '#fef3c7', color: '#92400e' },
  CODE:     { bg: '#fce7f3', color: '#9d174d' },
};

export default function AssetsTab({ orgId }) {
  const { t } = useTranslation();
  const LAYER_LABELS = {
    ALL: t('AssetsTab.layerAll'),
    COMPUTE: t('AssetsTab.layerCompute'),
    IDENTITY: t('AssetsTab.layerIdentity'),
    NETWORK: t('AssetsTab.layerNetwork'),
    DATABASE: t('AssetsTab.layerDatabase'),
    CODE: t('AssetsTab.layerCode'),
  };
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [layer, setLayer] = useState('ALL');
  const [provider, setProvider] = useState('ALL');
  const [providers, setProviders] = useState([]); // [{ name, count }] — vendors with assets
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchAssets = useCallback(() => {
    setLoading(true);
    const params = { page, size: 50 };
    if (layer !== 'ALL') params.layer = layer;
    if (provider !== 'ALL') params.provider = provider;
    if (orgId) params.orgId = orgId;
    axios.get('/iasm/assets', { params })
      .then(r => { setAssets(r.data.content); setTotal(r.data.totalElements); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, layer, provider, orgId]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Include/exclude an asset from the audit scope (e.g. a personal repo pulled in via personal auth).
  const setScope = (e, a, inScope) => {
    e.stopPropagation();
    const params = orgId ? { orgId } : {};
    axios.patch(`/iasm/assets/${a.id}/scope`, { inScope }, { params })
      .then(() => fetchAssets())
      .catch(() => {});
  };

  // Populate the vendor dropdown from the assets that actually exist for this org.
  useEffect(() => {
    axios.get('/iasm/assets/counts', { params: orgId ? { orgId } : {} })
      .then(r => setProviders(
        Object.entries(r.data.byCloudProvider || {}).map(([name, count]) => ({ name, count }))))
      .catch(() => {});
  }, [orgId]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {LAYERS.map(l => (
          <button
            key={l}
            onClick={() => { setLayer(l); setPage(0); }}
            style={{
              padding: '6px 14px', borderRadius: '6px', border: 'none',
              cursor: 'pointer', fontSize: '13px', fontWeight: layer === l ? 600 : 400,
              background: layer === l ? '#2563eb' : '#f3f4f6',
              color: layer === l ? '#fff' : '#374151',
            }}
          >{LAYER_LABELS[l] || l}</button>
        ))}
        <select
          value={provider}
          onChange={e => { setProvider(e.target.value); setPage(0); }}
          title={t('AssetsTab.filterByVendor')}
          style={{
            marginLeft: '8px', padding: '6px 10px', borderRadius: '6px',
            border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
            fontSize: '13px', color: '#374151',
          }}
        >
          <option value="ALL">{t('AssetsTab.allVendors')}</option>
          {providers.map(p => (
            <option key={p.name} value={p.name}>{t('AssetsTab.vendorOption', { name: p.name, count: p.count })}</option>
          ))}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7280', alignSelf: 'center' }}>
          {t('AssetsTab.assetCount', { count: total })}
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />{t('AssetsTab.loadingAssets')}
        </div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[
                  ['name', t('AssetsTab.colName')],
                  ['type', t('AssetsTab.colType')],
                  ['layer', t('AssetsTab.colLayer')],
                  ['cloud', t('AssetsTab.colCloud')],
                  ['region', t('AssetsTab.colRegion')],
                  ['risk', t('AssetsTab.colRisk')],
                  ['blastRadius', t('AssetsTab.colBlastRadius')],
                  ['scope', t('AssetsTab.colScope')],
                ].map(([k, h]) => (
                  <th key={k} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assets.map(a => (
                <React.Fragment key={a.id}>
                  <tr
                    onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                    style={{ cursor: 'pointer', borderBottom: '1px solid #f3f4f6', background: expanded === a.id ? '#eff6ff' : '#fff' }}
                  >
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{a.displayName || a.name}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{a.externalId?.substring(0, 50)}</div>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{a.assetType?.replace(/_/g, ' ')}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                        ...(LAYER_BADGE[a.layer] || { bg: '#f3f4f6', color: '#374151' }),
                        background: (LAYER_BADGE[a.layer] || {}).bg,
                        color: (LAYER_BADGE[a.layer] || {}).color,
                      }}>{a.layer}</span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{a.cloudProvider}</td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>{a.region || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <RiskBadge score={a.riskScore} />
                    </td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{a.blastRadius}</td>
                    <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                      {a.inScope === false ? (
                        <button onClick={e => setScope(e, a, true)}
                          title={t('AssetsTab.excludedTitle')}
                          style={{ padding: '3px 9px', borderRadius: '5px', border: '1px solid #fecaca',
                                   background: '#fef2f2', color: '#b91c1c', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
                          {t('AssetsTab.excluded')}
                        </button>
                      ) : (
                        <button onClick={e => setScope(e, a, false)}
                          title={t('AssetsTab.inScopeTitle')}
                          style={{ padding: '3px 9px', borderRadius: '5px', border: '1px solid #d1d5db',
                                   background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                          {t('AssetsTab.inScope')}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === a.id && a.metadata && (
                    <tr style={{ background: '#f9fafb' }}>
                      <td colSpan={8} style={{ padding: '12px 14px' }}>
                        <pre style={{ margin: 0, fontSize: '11px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(a.metadata, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {assets.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>{t('AssetsTab.noAssets')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {total > 50 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: page === 0 ? 'default' : 'pointer', background: '#fff' }}>
            {t('AssetsTab.prev')}
          </button>
          <span style={{ alignSelf: 'center', fontSize: '13px', color: '#6b7280' }}>
            {t('AssetsTab.pageOf', { current: page + 1, total: Math.ceil(total / 50) })}
          </span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 50 >= total}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: (page + 1) * 50 >= total ? 'default' : 'pointer', background: '#fff' }}>
            {t('AssetsTab.next')}
          </button>
        </div>
      )}
    </div>
  );
}

function RiskBadge({ score }) {
  const color = score >= 80 ? '#dc2626' : score >= 50 ? '#ea580c' : score >= 20 ? '#d97706' : '#6b7280';
  return (
    <span style={{ fontWeight: 700, color, fontSize: '13px' }}>{score}</span>
  );
}
